import React, { useMemo } from "react";
import { shallowEqual } from "react-redux";
import { pdpSectionSelector } from "../../../utils/pdpUtils";
import { useUscSelector } from "../../../redux/store";
import { PdpSection, ScrollTargetSelector } from "../../../utils/constants";
import Highlights from "./highlights/Highlights";
import CarDetails from "./car-details/CarDetails";
import PdpServices from "./services/PdpServices";
import HorizontalMenu from "./common/HorizontalMenu";
import UscBasket from "../common/basket/UscBasket";
import UscVehicleInformation from "../common/basket/common/UscVehicleInformation";
import Carousel from "./carousel/Carousel";
import Container from "../styles/atoms/Container";
import Content from "../styles/atoms/Content";
import Sections from "../styles/atoms/Sections";
import Footer from "../styles/atoms/Footer";
import Text from "../styles/atoms/Text";
import AccessoriesContainer from "./accessories/AccessoriesContainer";
import * as Styles from "./PdpContainer.styles";
import { UscTrackingLocation } from "../../../utils/tracking";
import PageFinanceDisclaimer from "../common/PageFinanceDisclaimer";
import { enableUscMonthlyRate } from "../../../utils/settings";
import { UscContext } from "../../../../shared-logic/types/UscCommonTypes";
import { useUscOptionalLabel, useUscLabel } from "../../../utils/labels";
import { useComponentRegistry } from "../componentRegistry";
import HubBanner from "./dealer/HubBanner";

const PdpContainer = (): JSX.Element | null => {
    const vehicle = useUscSelector((state) => state.vehicle);
    const uscSettings = useUscSelector((state) => state.uscSettings);
    const commonSettings = useUscSelector((state) => state.commonSettings);
    const visiblePdpSections = useUscSelector(pdpSectionSelector, shallowEqual);
    const vehicleAggregationsEnabled = useUscSelector((state) => state.uscSettings.enableVehicleAggregations);
    const carouselDisclaimer = useUscOptionalLabel(
        vehicle.type === UscContext.Used ? "uscPdpUsedCarouselDisclaimer" : "uscPdpStockCarouselDisclaimer",
    );
    const showFinanceDisclaimer = enableUscMonthlyRate(commonSettings, uscSettings, vehicle.result, vehicle.type);

    const ariaPdpFooterLabel = useUscLabel("uscAriaPdpFooter");

    const { showSimilarCars } = uscSettings;
    const { result } = vehicle;

    const PdpDealer = useComponentRegistry("PdpDealer");
    const Breadcrumbs = useComponentRegistry("Breadcrumbs");
    const SimilarCars = useComponentRegistry("SimilarCars");
    const Sidebar = useComponentRegistry("Sidebar");

    // all content (except the horizontal menu itself) should be wrapped in a div with a scroll target classname
    return useMemo(
        () => (
            <>
                <Container variant="container-reverse">
                    <Sidebar variant="basket">
                        <UscBasket />
                    </Sidebar>
                    <Content>
                        <Breadcrumbs />
                        <Carousel />
                        {carouselDisclaimer && !!result?.images.length && (
                            <Styles.CarouselDisclaimer>
                                <Text tag="div" variant="paragraph-small-book" color="platinum-dark">
                                    {carouselDisclaimer}
                                </Text>
                            </Styles.CarouselDisclaimer>
                        )}

                        {showFinanceDisclaimer && <PageFinanceDisclaimer />}
                        <HorizontalMenu />
                        <Styles.VehicleInformationMobileWrapper>
                            <UscVehicleInformation trackingLocation={UscTrackingLocation.Pdp} inPdpContainer />
                        </Styles.VehicleInformationMobileWrapper>
                        <Sections variant="main">
                            {visiblePdpSections.map((pdpSection) => {
                                let component = null;
                                let scrollTargetSelector: null | ScrollTargetSelector = null;

                                // Make sure to update 'scrollTargetIndexMap' in case the order of these sections is updated
                                switch (pdpSection) {
                                    case PdpSection.Highlights:
                                        scrollTargetSelector = ScrollTargetSelector.Highlights;
                                        component = <Highlights />;
                                        break;
                                    case PdpSection.Services:
                                        scrollTargetSelector = ScrollTargetSelector.Services;
                                        component = <PdpServices />;
                                        break;
                                    case PdpSection.Accessories:
                                        scrollTargetSelector = ScrollTargetSelector.Accessories;
                                        component = <AccessoriesContainer />;
                                        break;
                                    case PdpSection.CarDetails:
                                        scrollTargetSelector = ScrollTargetSelector.CarDetails;
                                        component = <CarDetails />;
                                        break;
                                    case PdpSection.DealerInfo:
                                        scrollTargetSelector = ScrollTargetSelector.DealerInfo;
                                        component = <PdpDealer />;
                                        break;
                                }

                                return (
                                    <div key={pdpSection}>
                                        {!!scrollTargetSelector && (
                                            <span id={scrollTargetSelector} tabIndex={-1}>
                                                {component.key}
                                            </span>
                                        )}
                                        {component}
                                    </div>
                                );
                            })}
                            {/* The hub banner is added as separate section like this as this is not part of the Horizontal Menu so not a real section */}
                            {result?.isHubCar && !vehicleAggregationsEnabled && <HubBanner />}
                        </Sections>
                    </Content>
                </Container>
                {!!result && (
                    <Footer variant="page-footer" aria-label={ariaPdpFooterLabel}>
                        <Container variant="container">
                            {showSimilarCars && (
                                <SimilarCars
                                    vehicleForSaleId={result.id}
                                    disableFavourite={false}
                                    context={vehicle.type}
                                />
                            )}
                        </Container>
                    </Footer>
                )}
            </>
        ),
        [!!result, showSimilarCars, visiblePdpSections.length],
    );
};

export default PdpContainer;
